import { useState, useEffect } from "react";
import { Phone } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { SponsorListSidebar } from "./SponsorListSidebar";
import { SponsorDetailView } from "./SponsorDetailView";
import {
  FollowUpStatusBadge,
  getPriorityFromDate,
} from "./FollowUpStatusBadge";

/* ── Compact card for pulled batch sidebar ── */
function BatchSidebarCard({ sponsor, isSelected, onClick }) {
  const priority = getPriorityFromDate(
    sponsor.followUp?.nextActionDate ?? sponsor.nextActionDate,
  );

  const dotClass =
    {
      overdue: "bg-red-500",
      today: "bg-amber-500",
      upcoming: "bg-green-500",
      none: "bg-muted-foreground/30",
    }[priority] ?? "bg-muted-foreground/30";

  const remaining =
    sponsor.remaining ?? sponsor.amountPromised - sponsor.paidAmount ?? 0;

  return (
    <div
      onClick={onClick}
      className={cn(
        "flex items-start gap-2.5 px-3 py-2.5 border-b cursor-pointer hover:bg-blue-50/40 transition-colors",
        isSelected && "bg-blue-50 border-l-2 border-l-primary",
      )}
    >
      <div className={cn("w-2 h-2 rounded-full mt-1 shrink-0", dotClass)} />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium truncate leading-snug">
          {sponsor.sponsorName}
        </p>
        <p className="text-[10px] text-muted-foreground truncate">
          {sponsor.mahalluName || sponsor.place || "—"}
        </p>
        <p className="text-[10px] font-semibold tabular-nums text-amber-600 mt-0.5">
          ₹{Math.max(remaining, 0).toLocaleString()} left
        </p>
      </div>
      <FollowUpStatusBadge
        status={sponsor.followUp?.status ?? sponsor.followUpStatus}
        className="text-[8px] shrink-0 mt-0.5"
      />
    </div>
  );
}

export function ActiveWorkspace({
  // Queue mode (no batch)
  sponsors = [],
  isLoading,
  // Workspace / batch mode
  sponsor, // currently viewed sponsor (controlled by parent)
  batch = [], // all pulled sponsors
  onSelectFromBatch, // parent updates workspaceSponsor
  onLogSuccess, // parent updates workspaceBatch with new status
  onPaymentRecorded, // parent refreshes sponsor data after payment
}) {
  const isBatchMode = batch.length > 0;

  /* ── Queue mode: internal selection state ── */
  const [selectedSponsor, setSelectedSponsor] = useState(null);

  useEffect(() => {
    if (!isBatchMode && !selectedSponsor && sponsors.length > 0) {
      setSelectedSponsor(sponsors[0]);
    }
  }, [sponsors, selectedSponsor, isBatchMode]);

  const currentSponsor = isBatchMode
    ? sponsor
    : selectedSponsor
      ? sponsors.find((s) => s._id === selectedSponsor._id) || selectedSponsor
      : null;

  /* ── Loading skeleton (queue mode only) ── */
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] gap-0 border rounded-lg overflow-hidden">
        <div className="border-r space-y-0">
          <div className="px-2 py-2">
            <Skeleton className="h-8 w-full rounded-md" />
          </div>
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="px-3 py-2.5 border-b">
              <div className="flex gap-2">
                <Skeleton className="h-2 w-2 rounded-full mt-1" />
                <div className="flex-1 space-y-1.5">
                  <Skeleton className="h-3 w-2/3" />
                  <Skeleton className="h-2.5 w-1/2" />
                </div>
              </div>
            </div>
          ))}
        </div>
        <div className="p-4 space-y-4">
          <Skeleton className="h-5 w-1/3" />
          <Skeleton className="h-3 w-1/2" />
          <Skeleton className="h-24 rounded-lg" />
          <Skeleton className="h-8 w-full" />
        </div>
      </div>
    );
  }

  /* ── Queue mode empty state ── */
  if (!isBatchMode && sponsors.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Phone className="h-10 w-10 text-muted-foreground/30 mb-3" />
        <p className="text-sm text-muted-foreground">
          No sponsors in your workspace. Pull sponsors from the queue above.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-[300px_1fr] border rounded-lg overflow-hidden min-h-[500px]">
      {/* ── Left sidebar ── */}
      <div className="border-r h-[calc(100vh-280px)] flex flex-col">
        {isBatchMode ? (
          <>
            {/* Batch header */}
            <div className="px-3 py-2.5 border-b bg-muted/30 shrink-0">
              <p className="text-[11px] font-semibold uppercase tracking-widest text-muted-foreground">
                Pulled Batch
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {batch.length} sponsor{batch.length !== 1 ? "s" : ""}
              </p>
            </div>
            {/* Batch sponsor list */}
            <div className="flex-1 overflow-y-auto">
              {batch.map((sp) => (
                <BatchSidebarCard
                  key={sp._id}
                  sponsor={sp}
                  isSelected={currentSponsor?._id === sp._id}
                  onClick={() => onSelectFromBatch?.(sp)}
                />
              ))}
            </div>
          </>
        ) : (
          <SponsorListSidebar
            sponsors={sponsors}
            selectedId={currentSponsor?._id}
            onSelect={setSelectedSponsor}
          />
        )}
      </div>

      {/* ── Right detail panel ── */}
      <div className="p-4 h-[calc(100vh-280px)] overflow-y-auto">
        <SponsorDetailView
          sponsor={currentSponsor}
          onLogSuccess={onLogSuccess}
          onPaymentRecorded={onPaymentRecorded}
        />
      </div>
    </div>
  );
}
